import { useMemo } from 'react'
import {Navigate, useParams} from 'react-router-dom'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
    toggleCompleteSaga,
    updateTaskSaga
} from '../../../store/tasks/tasksSlice'
import { selectAllTasks, selectTasksLoading } from '../../../store/tasks/tasksSelectors'
import { selectAllProjects, selectProjectsLoading } from '../../../store/projects/projectsSelectors'
import { TextH2 } from '../../ui/Text'
import Section from '../../app/Section/Section'
import Empty from '../../ui/Empty/Empty'
import PlateLoader from '../../ui/Loader/PlateLoader'
import css from './WorkspaceMyTasksPage.module.css'

const WorkspaceMyTasksPage = () => {
    const dispatch = useAppDispatch()
    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const { workspace, loading } = useWorkspace()
    const currentUserId = useAppSelector(state => state.auth?.id)

    const allTasks = useAppSelector(selectAllTasks)
    const projects = useAppSelector(selectAllProjects)
    const tasksLoading = useAppSelector(selectTasksLoading)
    const projectsLoading = useAppSelector(selectProjectsLoading)

    // Only consider tasks from projects the user can actually access. Tasks are loaded
    // workspace-wide (read = any workspace member), but projects are loaded only where
    // the user is a shared member. A task in a project the user doesn't belong to can't
    // be opened or toggled (rules deny the write), so don't surface it here.
    const accessibleProjectIds = useMemo(() =>
        new Set(projects.map((p: any) => p.id)),
        [projects]
    )

    // Split tasks into assigned and created (open tasks + recently completed)
    const assignedTasks = useMemo(() =>
        allTasks.filter((t: any) => t.assigneeId === currentUserId && accessibleProjectIds.has(t.projectId)),
        [allTasks, currentUserId, accessibleProjectIds]
    )

    const createdTasks = useMemo(() =>
        allTasks.filter((t: any) => t.ownerId === currentUserId && accessibleProjectIds.has(t.projectId)),
        [allTasks, currentUserId, accessibleProjectIds]
    )

    const groupTasksByProject = (tasks: typeof allTasks) => {
        const grouped: Record<string, typeof allTasks> = {}
        tasks.forEach((task: any) => {
            if (!grouped[task.projectId]) {
                grouped[task.projectId] = []
            }
            grouped[task.projectId].push(task)
        })
        // Drop project groups where every task is already completed — a project with no
        // open work shouldn't show up on My tasks just to render an empty "Completed
        // sections" expander.
        Object.keys(grouped).forEach((projectId) => {
            const hasOpenTask = grouped[projectId].some((task: any) => !task.isCompleted)
            if (!hasOpenTask) {
                delete grouped[projectId]
            }
        })
        return grouped
    }

    const getProject = (projectId: string) => {
        return projects.find((p: any) => p.id === projectId)
    }

    const handleTaskToggleComplete = (taskId: string, isCompleted: boolean) => {
        dispatch(toggleCompleteSaga({ taskId, isCompleted }))
    }

    const handleTaskAssign = (taskId: string, assigneeId: string | null) => {
        dispatch(updateTaskSaga({ taskId, data: { assigneeId } }))
    }

    if (loading || tasksLoading || projectsLoading) {
        return <PlateLoader fullscreen />
    }

    if (!workspace || workspace.workspaceUrlId !== workspaceUrlId) {
        return <Navigate to={"/"} />
    }

    const assignedByProject = groupTasksByProject(assignedTasks)
    const createdByProject = groupTasksByProject(createdTasks)

    const hasAssigned = Object.keys(assignedByProject).length > 0
    const hasCreated = Object.keys(createdByProject).length > 0

    if (!hasAssigned && !hasCreated) {
        return (
            <div className={css.container}>
                <Empty title="My tasks" text="There's nothing here." />
            </div>
        )
    }

    return (
        <div className={css.container}>
            {hasAssigned && (
                <div className={css.section}>
                    <div className={css.sectionTitle}>
                        <TextH2>Assigned to me</TextH2>
                    </div>
                    <div className={css.sectionContent}>
                        <div className={css.lists}>
                            {Object.entries(assignedByProject)
                                .sort(([a], [b]) => (getProject(a)?.name || '').localeCompare(getProject(b)?.name || ''))
                                .map(([projectId, tasks]) => {
                                const project = getProject(projectId)
                                return (
                                    <Section
                                        key={projectId}
                                        title={project?.name || 'Untitled'}
                                        titleHref={`/${workspaceUrlId}/projects/${projectId}`}
                                        tasks={tasks}
                                        onTaskToggleComplete={handleTaskToggleComplete}
                                        onTaskAssign={handleTaskAssign}
                                        showCompletedTasks={true}
                                        completedSortBy="recent"
                                    />
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {hasAssigned && hasCreated && <div className={css.divider} />}

            {hasCreated && (
                <div className={css.section}>
                    <div className={css.sectionTitle}>
                        <TextH2>Created by me</TextH2>
                    </div>
                    <div className={css.sectionContent}>
                        <div className={css.lists}>
                            {Object.entries(createdByProject)
                                .sort(([a], [b]) => (getProject(a)?.name || '').localeCompare(getProject(b)?.name || ''))
                                .map(([projectId, tasks]) => {
                                const project = getProject(projectId)
                                return (
                                    <Section
                                        key={projectId}
                                        title={project?.name || 'Untitled'}
                                        titleHref={`/${workspaceUrlId}/projects/${projectId}`}
                                        tasks={tasks}
                                        onTaskToggleComplete={handleTaskToggleComplete}
                                        onTaskAssign={handleTaskAssign}
                                        showCompletedTasks={true}
                                        completedSortBy="recent"
                                    />
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default WorkspaceMyTasksPage
